// Dashboard : jauges billetterie, objectifs, publications de la semaine, tâches
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { History, Pencil, Check, X, ArrowRight, Cake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CircularGauge } from "@/components/CircularGauge";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";
import { UserAvatar } from "@/components/UserAvatar";
import { VOIES, STATUTS_PUBLICATION, PLATEFORMES } from "@/lib/constants";
import { formatLong, formatCourt, formatHeure, formatDateHeure, aujourdHuiISO, estEnRetard } from "@/lib/dates";

const Carte = ({ children, className = "", testId }) => (
  <div className={`bg-card rounded-2xl border border-border p-6 carte-douce apparition ${className}`} data-testid={testId}>
    {children}
  </div>
);

// ——— Formulaire de saisie des ventes (admin/media) ———
const SaisieVentes = ({ onSaved }) => {
  const [night, setNight] = useState("friday");
  const [sold, setSold] = useState("");
  const [date, setDate] = useState(aujourdHuiISO());
  const [envoi, setEnvoi] = useState(false);
  const [historique, setHistorique] = useState([]);

  const soumettre = async (e) => {
    e.preventDefault();
    setEnvoi(true);
    try {
      await api.post("/ticket-sales", { night, sold: Number(sold), date: `${date}T12:00` });
      toast.success("Chiffres de vente enregistrés");
      setSold("");
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setEnvoi(false);
    }
  };

  const chargerHistorique = async () => {
    const { data } = await api.get("/ticket-sales/entries", { params: { limit: 30 } });
    setHistorique(data);
  };

  return (
    <Carte testId="sales-entry-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif text-xl font-semibold">Saisir les ventes</h3>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="ghost" size="sm" onClick={chargerHistorique} className="gap-1.5 rounded-full" data-testid="sales-history-button">
              <History className="h-4 w-4" /> Historique
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Historique des saisies</DialogTitle>
              <DialogDescription>Toutes les mises à jour de ventes enregistrées par l'équipe.</DialogDescription>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto space-y-2" data-testid="sales-history-list">
              {historique.map((e) => (
                <div key={e.id} className="flex items-center justify-between rounded-xl bg-muted/50 px-4 py-2.5 text-sm">
                  <div>
                    <p className="font-medium">{e.night === "friday" ? "Vendredi 30 oct." : "Samedi 31 oct."} — {e.sold.toLocaleString("fr-BE")} places</p>
                    <p className="text-xs text-muted-foreground">{formatDateHeure(e.date)} · {e.saisi_par_nom || "API n8n"}</p>
                  </div>
                  <Badge variant={e.source === "api" ? "secondary" : "outline"}>{e.source === "api" ? "API" : "Manuel"}</Badge>
                </div>
              ))}
              {historique.length === 0 && <p className="text-sm text-muted-foreground">Aucune saisie.</p>}
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <form onSubmit={soumettre} className="space-y-3">
        <Select value={night} onValueChange={setNight}>
          <SelectTrigger className="rounded-xl" data-testid="sales-night-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="friday">Vendredi 30 octobre</SelectItem>
            <SelectItem value="saturday">Samedi 31 octobre</SelectItem>
          </SelectContent>
        </Select>
        <Input
          type="number"
          min="0"
          required
          placeholder="Total de places vendues"
          value={sold}
          onChange={(e) => setSold(e.target.value)}
          className="rounded-xl"
          data-testid="sales-sold-input"
        />
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl" data-testid="sales-date-input" />
        <Button type="submit" disabled={envoi || sold === ""} className="w-full rounded-full" data-testid="sales-submit-button">
          Enregistrer
        </Button>
      </form>
    </Carte>
  );
};

// ——— Bloc objectifs semaine / mois ———
const Objectifs = ({ objectifs, canEdit, onSaved }) => {
  const [edition, setEdition] = useState(null); // { type, valeur }

  const sauver = async () => {
    try {
      await api.put("/objectives", { type: edition.type, valeur: Number(edition.valeur) });
      toast.success("Objectif mis à jour");
      setEdition(null);
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Carte testId="objectives-card">
      <h3 className="font-serif text-xl font-semibold mb-4">Objectifs de vente</h3>
      <div className="space-y-5">
        {["semaine", "mois"].map((type) => {
          const o = objectifs?.[type] || { objectif: 0, realise: 0, ecart: 0 };
          const pct = o.objectif ? Math.min(100, (o.realise / o.objectif) * 100) : 0;
          return (
            <div key={type} data-testid={`objective-${type}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-medium capitalize">{type === "semaine" ? "Cette semaine" : "Ce mois"}</span>
                {edition?.type === type ? (
                  <span className="flex items-center gap-1.5">
                    <Input
                      type="number"
                      min="0"
                      value={edition.valeur}
                      onChange={(e) => setEdition({ ...edition, valeur: e.target.value })}
                      className="h-7 w-20 rounded-lg text-sm"
                      data-testid={`objective-${type}-input`}
                    />
                    <button onClick={sauver} className="p-1 text-success" data-testid={`objective-${type}-save`}><Check className="h-4 w-4" /></button>
                    <button onClick={() => setEdition(null)} className="p-1 text-muted-foreground"><X className="h-4 w-4" /></button>
                  </span>
                ) : (
                  <span className="flex items-center gap-2 text-sm">
                    <span className="font-semibold" data-testid={`objective-${type}-realise`}>{o.realise}</span>
                    <span className="text-muted-foreground">/ {o.objectif} billets</span>
                    {canEdit && (
                      <button onClick={() => setEdition({ type, valeur: o.objectif })} className="p-1 text-muted-foreground hover:text-foreground transition-colors" data-testid={`objective-${type}-edit`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </span>
                )}
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full bg-accent transition-[width] duration-700" style={{ width: `${pct}%` }} />
              </div>
              <p className={`text-xs mt-1 font-medium ${o.ecart >= 0 ? "text-success" : "text-destructive"}`}>
                Écart : {o.ecart >= 0 ? "+" : ""}{o.ecart}
              </p>
            </div>
          );
        })}
      </div>
    </Carte>
  );
};

const MOIS_FR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

const AnniversairesDuMois = ({ utilisateurs }) => {
  const mois = new Date().getMonth() + 1;
  const jourActuel = new Date().getDate();
  const liste = (utilisateurs || [])
    .filter((u) => u.anniversaire_mois === mois && u.anniversaire_jour)
    .sort((a, b) => a.anniversaire_jour - b.anniversaire_jour);
  if (liste.length === 0) return null;
  return (
    <div className="rounded-2xl border border-accent/40 bg-accent/15 px-5 py-4" data-testid="dashboard-birthdays">
      <p className="text-xs font-semibold uppercase tracking-wide inline-flex items-center gap-1.5">
        <Cake className="h-3.5 w-3.5" /> Anniversaires en {MOIS_FR[mois - 1]}
      </p>
      <div className="flex flex-wrap items-center gap-3 mt-2">
        {liste.map((u) => (
          <span key={u.id} className="inline-flex items-center gap-2 text-sm" data-testid={`dashboard-birthday-${u.id}`}>
            <UserAvatar user={u} size="sm" />
            {u.name}
            <span className={`text-xs ${u.anniversaire_jour === jourActuel ? "text-primary font-semibold" : "text-muted-foreground"}`}>
              {u.anniversaire_jour === jourActuel ? "aujourd'hui !" : `le ${u.anniversaire_jour}`}
            </span>
          </span>
        ))}
      </div>
      <Link to="/equipe" className="text-xs text-primary hover:underline inline-block mt-2.5">Voir l'équipe</Link>
    </div>
  );
};


export default function Dashboard() {  const { user } = useAuth();
  const [data, setData] = useState(null);
  const canEdit = ["admin", "media"].includes(user.role);

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  if (!data) {
    return <div className="py-24 text-center text-muted-foreground">Chargement du tableau de bord…</div>;
  }

  const nomUtilisateur = (id) => data.utilisateurs.find((u) => u.id === id);
  const basculerTache = async (tache) => {
    const statut = tache.statut === "termine" ? "a_faire" : "termine";
    try {
      await api.patch(`/tasks/${tache.id}`, { statut });
      charger();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <AnnouncementBanner
        annonces={data.annonces}
        onDismiss={(id) => setData({ ...data, annonces: data.annonces.filter((a) => a.id !== id) })}
      />

      <div>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold">Muraho, {user.name}.</h1>
        <p className="text-muted-foreground mt-1 first-letter:capitalize">{formatLong(new Date())}</p>
      </div>

      <AnniversairesDuMois utilisateurs={data.utilisateurs} />

      {/* ——— Billetterie ——— */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
        <Carte className="lg:col-span-4 flex items-center justify-center" testId="gauge-friday-card">
          <CircularGauge
            vendues={data.ventes.friday.vendues}
            objectif={data.ventes.friday.objectif}
            couleur="#B85C38"
            libelle="Vendredi 30 octobre"
            derniereMaj={data.ventes.friday.derniere_maj ? formatCourt(data.ventes.friday.derniere_maj) : null}
            testId="ticket-gauge-friday"
          />
        </Carte>
        <Carte className="lg:col-span-4 flex items-center justify-center" testId="gauge-saturday-card">
          <CircularGauge
            vendues={data.ventes.saturday.vendues}
            objectif={data.ventes.saturday.objectif}
            couleur="#D4A843"
            libelle="Samedi 31 octobre"
            derniereMaj={data.ventes.saturday.derniere_maj ? formatCourt(data.ventes.saturday.derniere_maj) : null}
            testId="ticket-gauge-saturday"
          />
        </Carte>
        <div className="lg:col-span-4 space-y-6">
          <Objectifs objectifs={data.objectifs} canEdit={canEdit} onSaved={charger} />
          {canEdit && <SaisieVentes onSaved={charger} />}
        </div>
      </div>

      {/* ——— Semaine éditoriale + tâches ——— */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Carte className="lg:col-span-7" testId="week-publications-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-xl font-semibold">Cette semaine</h3>
            <Link to="/calendrier" className="text-sm text-primary flex items-center gap-1 hover:gap-2 transition-[gap]">
              Calendrier <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="space-y-2.5">
            {data.publications_semaine.map((p) => {
              const voie = VOIES[p.voie];
              const resp = nomUtilisateur(p.responsable_id);
              return (
                <div key={p.id} className="flex items-center gap-3 rounded-xl bg-muted/40 px-4 py-3" data-testid={`week-publication-${p.id}`}>
                  <span className="h-9 w-1 rounded-full shrink-0" style={{ background: voie?.couleur }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.titre}</p>
                    <p className="text-xs text-muted-foreground first-letter:capitalize">
                      {formatCourt(p.date_publication)} à {formatHeure(p.date_publication)} · {voie?.label} · {p.plateformes.map((pl) => PLATEFORMES[pl]).join(", ")}
                    </p>
                  </div>
                  {resp && <UserAvatar user={resp} size="sm" />}
                  <Badge className={`${STATUTS_PUBLICATION[p.statut]?.classe} border-0 shrink-0`}>{STATUTS_PUBLICATION[p.statut]?.label}</Badge>
                </div>
              );
            })}
            {data.publications_semaine.length === 0 && (
              <p className="text-sm text-muted-foreground py-4">Aucune publication prévue ces 7 prochains jours.</p>
            )}
          </div>
        </Carte>

        <div className="lg:col-span-5 space-y-6">
          <Carte testId="my-tasks-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl font-semibold">Mes tâches</h3>
              <Link to="/taches" className="text-sm text-primary flex items-center gap-1 hover:gap-2 transition-[gap]">
                Tout voir <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <div className="space-y-2">
              {data.mes_taches.slice(0, 5).map((t) => (
                <label key={t.id} className="flex items-center gap-3 rounded-xl px-3 py-2 hover:bg-muted/40 transition-colors cursor-pointer" data-testid={`my-task-${t.id}`}>
                  <Checkbox checked={t.statut === "termine"} onCheckedChange={() => basculerTache(t)} />
                  <span className="flex-1 text-sm">{t.titre}</span>
                  {t.echeance && (
                    <span className={`text-xs ${estEnRetard(t.echeance) ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
                      {formatCourt(t.echeance)}
                    </span>
                  )}
                </label>
              ))}
              {data.mes_taches.length === 0 && <p className="text-sm text-muted-foreground py-2">Aucune tâche en cours. Murakoze !</p>}
            </div>
          </Carte>

          <Carte testId="overdue-tasks-card">
            <h3 className="font-serif text-xl font-semibold mb-4 text-destructive">En retard — équipe</h3>
            <div className="space-y-2">
              {data.taches_retard.slice(0, 5).map((t) => (
                <div key={t.id} className="flex items-center gap-3 rounded-xl bg-destructive/10 px-4 py-2.5" data-testid={`overdue-task-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{t.titre}</p>
                    <p className="text-xs text-destructive">Échéance : {formatCourt(t.echeance)}</p>
                  </div>
                  <div className="flex -space-x-2">
                    {t.assignes.map((id) => {
                      const u = nomUtilisateur(id);
                      return u ? <UserAvatar key={id} user={u} size="sm" /> : null;
                    })}
                  </div>
                </div>
              ))}
              {data.taches_retard.length === 0 && <p className="text-sm text-muted-foreground py-2">Aucune tâche en retard.</p>}
            </div>
          </Carte>
        </div>
      </div>
    </div>
  );
}
